import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "~/foundation/utils/cn";

export interface ScrollableProps extends HTMLAttributes<HTMLDivElement> {
  /** Fade shadows at the edges once there is content out of view. */
  shadow?: boolean;
  children?: ReactNode;
}

export const Scrollable = forwardRef<HTMLDivElement, ScrollableProps>(function Scrollable(
  { shadow = true, className, children, ...rest },
  forwardedRef,
) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el) {
      return;
    }
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  useEffect(() => {
    measure();
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return (
    /*
     * `flex-1 min-h-0` sizes this inside a flex column; `h-full` sizes it inside a grid or
     * block parent. Both are needed: with only the flex pair, a grid child (the scene outlet)
     * collapses to content height and never scrolls — it just gets clipped. In a flex column
     * `flex-basis: 0%` wins over `height: 100%`, so the two never fight.
     */
    <div className="relative h-full min-h-0 flex-1">
      {shadow && edges.top ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-3"
          style={{ background: "linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent)" }}
        />
      ) : null}
      <div
        ref={(node) => {
          innerRef.current = node;
          if (typeof forwardedRef === "function") {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
        }}
        onScroll={measure}
        className={cn("scrollbar-thin h-full overflow-y-auto overscroll-contain", className)}
        {...rest}
      >
        {children}
      </div>
      {shadow && edges.bottom ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-3"
          style={{ background: "linear-gradient(to top, rgb(0 0 0 / 10%), transparent)" }}
        />
      ) : null}
    </div>
  );
});
